import { ROLES, type Role } from '@cusp/core';
import type { DevIdentity } from '@cusp/contracts';

/**
 * The swappable dev identity IS one membership row.
 *
 * Identity in cusp is (user x tenant x role), never a user alone: `users` is
 * global and all tenant affiliation lives on `memberships`. The demo seed
 * proves why that matters — Sam Rivera holds two memberships (a UT learner and
 * an individual purchaser), so the same human appears twice in the picker with
 * completely different entitlements.
 */
export type Identity = DevIdentity;

/**
 * Where a stored identity came from — and therefore what it is worth.
 *
 * `'session'` was derived from a real cookie session (`adoptSessionIdentity`,
 * the login screen's picker). `'dev'` was picked in the dev identity switcher,
 * which makes it a HEADER-AUTH artefact: no credential was ever presented, the
 * API only honours it because the dev header fallback is on, and production
 * ignores it entirely.
 *
 * The two look identical in storage, which is the bug this distinction fixes:
 * with the switcher gated off, a dev pick left behind in localStorage kept
 * rendering "Signed in as …" on the public landing page for someone who had
 * never signed in. A dev identity must not outlive the switcher that made it.
 */
export type IdentitySource = 'dev' | 'session';

const STORAGE_KEY = 'cusp.dev-identity';

/**
 * Storage with an in-memory fallback.
 *
 * This keeps the module testable under vitest's default node environment, so
 * this package's suites need no jsdom dependency at all. It also makes the
 * store immune to Safari private mode, where `localStorage.setItem` throws.
 */
const memory = new Map<string, string>();
const store = {
  get(k: string): string | null {
    try {
      return globalThis.localStorage?.getItem(k) ?? memory.get(k) ?? null;
    } catch {
      return memory.get(k) ?? null;
    }
  },
  set(k: string, v: string): void {
    memory.set(k, v);
    try {
      globalThis.localStorage?.setItem(k, v);
    } catch {
      /* quota exceeded / private mode — the in-memory copy still holds */
    }
  },
  remove(k: string): void {
    memory.delete(k);
    try {
      globalThis.localStorage?.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

// Hydrated synchronously at module load so the very first render already has an
// identity — no flash of "nobody selected", no effect-driven second render.
let stored: Stored | null = load();
let transient: Identity | undefined;
let current: Identity | null = stored?.identity ?? null;
const listeners = new Set<() => void>();

interface Stored {
  identity: Identity;
  source: IdentitySource;
}

function load(): Stored | null {
  const raw = store.get(STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && 'identity' in parsed) {
      const o = parsed as { identity: unknown; source?: unknown };
      if (!isIdentity(o.identity)) return null;
      return { identity: o.identity, source: o.source === 'dev' ? 'dev' : 'session' };
    }
    // A bare identity, written before this envelope existed. Treat it as 'dev':
    // the key is `cusp.dev-identity` and the switcher was its original writer,
    // so this is what self-heals a browser that used the switcher before it was
    // gated. A real cookie session re-derives its own via adoptSessionIdentity,
    // so nothing durable is lost.
    return isIdentity(parsed) ? { identity: parsed, source: 'dev' } : null;
  } catch {
    return null;
  }
}

/** Anyone can hand-edit localStorage, so validate before trusting it. */
function isIdentity(v: unknown): v is Identity {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o['tenantId'] === 'string' &&
    typeof o['userId'] === 'string' &&
    ROLES.includes(o['role'] as Role)
  );
}

/** Stable reference between writes — required by `useSyncExternalStore`. */
export function getIdentity(): Identity | null {
  return transient ?? current;
}

/** Refreshing labels must retain whether this identity came from a credential. */
export function getIdentitySource(): IdentitySource | null {
  return transient ? null : (stored?.source ?? null);
}

/**
 * `source` defaults to `'session'` so every caller that presented a real
 * credential gets the durable kind without having to say so; only the dev
 * switcher opts into `'dev'`.
 */
export function setTransientIdentity(next: Identity | undefined): void {
  transient = next;
  notify();
}

export function hasTransientIdentity(): boolean {
  return transient !== undefined;
}

export function setIdentity(next: Identity | null, source: IdentitySource = 'session'): void {
  if (transient) return; // Demo adapters cannot overwrite the persisted real identity.
  // An idempotent re-write must not repartition the cache. `adoptSessionIdentity`
  // re-affirms the held membership on every `SessionControls` mount, and a
  // SessionControls that lives inside an identity-scoped, data-gated subtree
  // (the learner course overview) would otherwise oscillate forever: the write
  // fires `installIdentityCacheReset`, the reset blanks the subtree's data, the
  // subtree unmounts SessionControls, the data returns, it remounts and writes
  // again (#101). The cache partition is keyed by (user x tenant x role), so a
  // write that changes neither the identity tuple nor its source is a no-op —
  // `notify` (and its cache wipe) only makes sense when the acting identity or
  // its provenance actually changed.
  const unchanged = sameIdentity(current, next) && (next === null || stored?.source === source);
  current = next;
  stored = next === null ? null : { identity: next, source };
  if (stored) store.set(STORAGE_KEY, JSON.stringify(stored));
  else store.remove(STORAGE_KEY);
  if (!unchanged) notify();
}

/**
 * Forget an identity that only the dev switcher could have produced.
 *
 * Called when an app configures itself WITHOUT dev identities, so gating the
 * switcher also revokes what it left behind. A session-derived identity is
 * untouched — a real signed-in user is still signed in.
 */
export function dropDevIdentity(): void {
  if (stored?.source !== 'dev') return;
  setIdentity(null);
}

export function subscribeIdentity(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) fn();
}

/**
 * Two identities are the same acting identity when they name the same
 * (user x tenant x role) — the exact tuple `identityScope` partitions the cache
 * by. Cheap field compare, not deep-equal: `Identity` is a flat record every
 * builder (`membershipIdentity`) produces deterministically.
 */
function sameIdentity(a: Identity | null, b: Identity | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return (
    a.tenantId === b.tenantId &&
    a.userId === b.userId &&
    a.role === b.role &&
    a.tenantName === b.tenantName &&
    a.tenantSlug === b.tenantSlug &&
    a.userName === b.userName &&
    a.email === b.email
  );
}

/**
 * The identity headers `apps/api/src/http/context.ts` reads.
 *
 * Only `x-tenant-id` still MEANS anything once a bearer access token is
 * present: the token names the user, and the acting role is read from the
 * `memberships` row, so `x-user-id` and `x-role` are accepted and ignored. They
 * are still sent because the dev header fallback — the identity switcher, and
 * the API's whole test matrix — is exactly the case where no token exists.
 */
export function authHeaders(): Record<string, string> {
  return authHeadersFor(getIdentity());
}

/**
 * The same headers for a NAMED membership rather than the selected one.
 *
 * For a write that was queued under one identity and is being sent after the
 * picker has already moved on — the engagement beacon's final flush — where
 * `authHeaders()` would file it under the institution the learner has just
 * switched TO. The tenant half is the one that matters: it is a claim the API
 * proves against `memberships`, and a wrong one puts a learner's activity in
 * another institution's report.
 */
export function authHeadersFor(identity: Identity | null): Record<string, string> {
  if (identity === null) return {};
  return {
    'x-tenant-id': identity.tenantId,
    'x-user-id': identity.userId,
    'x-role': identity.role,
  };
}

/**
 * Cache-partition key.
 *
 * Every identity-dependent query key is prefixed with this, so data fetched as
 * one identity is physically unreachable as another. That closes the
 * cross-tenant staleness bug BY CONSTRUCTION rather than by remembering to
 * clear a cache on every switch — which matters here more than in a typical
 * app, since demonstrating tenant isolation is the whole point of these
 * screens. Role is part of the partition because it can change what the API
 * returns.
 */
export function identityScope(id: Identity | null): string {
  return id === null ? 'anon' : `${id.tenantId}:${id.userId}:${id.role}`;
}

/** Two tabs silently disagreeing about who you are is exactly the confusion
 * this mechanism exists to prevent, so follow identity changes across tabs. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    stored = load();
    current = stored?.identity ?? null;
    notify();
  });
}
